import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import {
    Alert,
    Button,
    Card,
    Drawer,
    Form,
    Input,
    Layout,
    Modal,
    Space,
    Tag,
    Typography,
} from 'antd';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { FC, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
    DocumentAuditClaimedCertificationDto,
    DocumentAuditClaimedVaccinationDto,
    DocumentAuditControllerListDocumentTypeEnum,
    DocumentAuditControllerListLlmDecisionEnum,
    DocumentAuditControllerListRequest,
    DocumentAuditDecisionRequestDtoDecisionEnum,
    DocumentAuditDetailDto,
    DocumentAuditDetailDtoDocumentTypeEnum,
    DocumentAuditListItemDto,
    DocumentAuditListItemDtoDocumentTypeEnum,
    DocumentAuditListItemDtoStatusEnum,
    DocumentAuditProfileDto,
    DocumentAuditValidationResultDto,
    DocumentAuditValidationResultDtoDecisionEnum,
} from '@clh/api-client';
import { unwrapError, useApiClient } from '@clh/ui';
import {
    CERTIFICATION_DISPLAY_NAMES,
    VACCINATION_TYPE_DISPLAY_NAMES,
} from '@clh/util';

import { useToast } from '../hooks/use-toast';
import { Content, Header } from '../layout';
import Spinner from '../spinner/spinner';
import { FetchRecordTable, FilterSelect } from '../table';

dayjs.extend(relativeTime);

const formatName = (profile: DocumentAuditProfileDto | null): string | null => {
    if (!profile) return null;
    const joined = [profile.firstName, profile.lastName]
        .filter(Boolean)
        .join(' ');
    return joined || null;
};

const formatClaimedCodes = (record: DocumentAuditListItemDto): string[] => {
    const displayNames =
        record.documentType ===
        DocumentAuditListItemDtoDocumentTypeEnum.Certification
            ? CERTIFICATION_DISPLAY_NAMES
            : VACCINATION_TYPE_DISPLAY_NAMES;
    const names = record.claimedCodes.map(
        (code) => displayNames[code] ?? code.replace(/_/g, ' ')
    );
    return [...new Set(names)];
};

const STATUS_META: Record<
    DocumentAuditListItemDtoStatusEnum,
    { color: string; label: string }
> = {
    [DocumentAuditListItemDtoStatusEnum.NeedsReview]: {
        color: 'gold',
        label: 'Not reviewed',
    },
    [DocumentAuditListItemDtoStatusEnum.Approved]: {
        color: 'green',
        label: 'Approved',
    },
    [DocumentAuditListItemDtoStatusEnum.Rejected]: {
        color: 'red',
        label: 'Rejected',
    },
};

const STATUS_OPTIONS = Object.entries(STATUS_META).map(
    ([value, { label }]) => ({
        value: value as DocumentAuditListItemDtoStatusEnum,
        label,
    })
);

const LLM_DECISION_LABELS: Record<
    DocumentAuditControllerListLlmDecisionEnum,
    string
> = {
    [DocumentAuditControllerListLlmDecisionEnum.None]: 'Not run',
    [DocumentAuditControllerListLlmDecisionEnum.Rejected]: 'Rejected',
    [DocumentAuditControllerListLlmDecisionEnum.Approved]: 'Approved',
    [DocumentAuditControllerListLlmDecisionEnum.Error]: 'Failed',
};

const LLM_DECISION_OPTIONS = Object.entries(LLM_DECISION_LABELS).map(
    ([value, label]) => ({
        value: value as DocumentAuditControllerListLlmDecisionEnum,
        label,
    })
);

// Only nurse-credential document types are auditable, so we surface just those.
const DOCUMENT_TYPE_LABELS: Partial<
    Record<DocumentAuditControllerListDocumentTypeEnum, string>
> = {
    [DocumentAuditControllerListDocumentTypeEnum.Vaccination]: 'Vaccination',
    [DocumentAuditControllerListDocumentTypeEnum.Certification]:
        'Certification',
};

const DOCUMENT_TYPE_OPTIONS = Object.entries(DOCUMENT_TYPE_LABELS).map(
    ([value, label]) => ({
        value: value as DocumentAuditControllerListDocumentTypeEnum,
        label,
    })
);

const EXPIRY_LABELS: Record<'false' | 'true', string> = {
    false: 'Not expired',
    true: 'Expired',
};

const EXPIRY_OPTIONS = Object.entries(EXPIRY_LABELS).map(([value, label]) => ({
    value,
    label,
}));

const OUTDATED_LABELS: Record<'false' | 'true', string> = {
    false: 'Not outdated',
    true: 'Outdated',
};

const OUTDATED_OPTIONS = Object.entries(OUTDATED_LABELS).map(
    ([value, label]) => ({
        value,
        label,
    })
);

export const DocumentAudits: FC = () => {
    const api = useApiClient();
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [refetchToken, setRefetchToken] = useState(0);

    if (!api) {
        return <Spinner />;
    }

    return (
        <Layout>
            <Header>
                <h2>Document Audits</h2>
            </Header>
            <Content>
                <FetchRecordTable
                    key={refetchToken}
                    rowKey="storedDocumentId"
                    paginated
                    cacheKey="documentAuditControllerList"
                    defaultParams={{
                        status: DocumentAuditListItemDtoStatusEnum.NeedsReview,
                        expired: 'false',
                        outdated: 'false',
                    }}
                    getRecords={(
                        params?: Partial<DocumentAuditControllerListRequest>
                    ) => {
                        const { expired, outdated, ...rest } = params ?? {};
                        return api.documentAuditControllerList({
                            ...rest,
                            expired:
                                expired === undefined
                                    ? undefined
                                    : `${expired}` === 'true',
                            outdated:
                                outdated === undefined
                                    ? undefined
                                    : `${outdated}` === 'true',
                        });
                    }}
                    filters={{
                        search: 'search',
                        searchPlaceholder: 'Enter name, email, or note text',
                        filters: {
                            status: {
                                label: 'Human review',
                                control: ({ onApply, onCancel }) => (
                                    <FilterSelect
                                        onApply={onApply}
                                        onCancel={onCancel}
                                        style={{ width: 180 }}
                                        placeholder="Human review"
                                        options={STATUS_OPTIONS}
                                    />
                                ),
                                displayValue: (
                                    value: DocumentAuditListItemDtoStatusEnum
                                ) => STATUS_META[value].label,
                            },
                            documentType: {
                                label: 'Document type',
                                control: ({ onApply, onCancel }) => (
                                    <FilterSelect
                                        onApply={onApply}
                                        onCancel={onCancel}
                                        style={{ width: 180 }}
                                        placeholder="Document type"
                                        options={DOCUMENT_TYPE_OPTIONS}
                                    />
                                ),
                                displayValue: (
                                    value: DocumentAuditControllerListDocumentTypeEnum
                                ) => DOCUMENT_TYPE_LABELS[value] ?? value,
                            },
                            llmDecision: {
                                label: 'LLM pre-screen',
                                control: ({ onApply, onCancel }) => (
                                    <FilterSelect
                                        onApply={onApply}
                                        onCancel={onCancel}
                                        style={{ width: 180 }}
                                        placeholder="LLM pre-screen"
                                        options={LLM_DECISION_OPTIONS}
                                    />
                                ),
                                displayValue: (
                                    value: DocumentAuditControllerListLlmDecisionEnum
                                ) => LLM_DECISION_LABELS[value],
                            },
                            expired: {
                                label: 'Expiration',
                                control: ({ onApply, onCancel }) => (
                                    <FilterSelect
                                        onApply={onApply}
                                        onCancel={onCancel}
                                        style={{ width: 180 }}
                                        placeholder="Expiration"
                                        options={EXPIRY_OPTIONS}
                                    />
                                ),
                                displayValue: (value: 'false' | 'true') =>
                                    EXPIRY_LABELS[value],
                            },
                            outdated: {
                                label: 'Outdated',
                                control: ({ onApply, onCancel }) => (
                                    <FilterSelect
                                        onApply={onApply}
                                        onCancel={onCancel}
                                        style={{ width: 180 }}
                                        placeholder="Outdated"
                                        options={OUTDATED_OPTIONS}
                                    />
                                ),
                                displayValue: (value: 'false' | 'true') =>
                                    OUTDATED_LABELS[value],
                            },
                        },
                    }}
                    onRow={(record: DocumentAuditListItemDto) => ({
                        onClick: () => setSelectedId(record.storedDocumentId),
                        style: { cursor: 'pointer' },
                    })}
                    columns={[
                        {
                            title: 'Nurse',
                            dataIndex: 'ownedBy',
                            key: 'ownedBy',
                            render: (ownedBy: DocumentAuditProfileDto | null) =>
                                formatName(ownedBy) ?? (
                                    <Typography.Text type="secondary">
                                        Unknown
                                    </Typography.Text>
                                ),
                        },
                        {
                            title: 'Document type',
                            dataIndex: 'documentType',
                            key: 'documentType',
                            render: (
                                value: string,
                                record: DocumentAuditListItemDto
                            ) => {
                                const kind =
                                    DOCUMENT_TYPE_LABELS[
                                        value as DocumentAuditControllerListDocumentTypeEnum
                                    ] ?? value.replace(/_/g, ' ');
                                const claimed = formatClaimedCodes(record);
                                if (claimed.length === 0) {
                                    return kind;
                                }
                                return (
                                    <Space direction="vertical" size={0}>
                                        {claimed.map((name) => (
                                            <span key={name}>
                                                {kind}: {name}
                                            </span>
                                        ))}
                                    </Space>
                                );
                            },
                        },
                        {
                            title: 'Uploaded',
                            dataIndex: 'uploadedAt',
                            key: 'uploadedAt',
                            render: (value: Date) => (
                                <span
                                    title={dayjs(value).format(
                                        'YYYY-MM-DD HH:mm'
                                    )}
                                >
                                    {dayjs(value).fromNow()}
                                </span>
                            ),
                        },
                        {
                            title: 'LLM pre-screen',
                            dataIndex: 'llmResult',
                            key: 'llmResult',
                            render: (
                                result: DocumentAuditValidationResultDto | null
                            ) => {
                                if (!result) {
                                    return (
                                        <Typography.Text type="secondary">
                                            Not run
                                        </Typography.Text>
                                    );
                                }
                                if (
                                    result.decision ===
                                    DocumentAuditValidationResultDtoDecisionEnum.Error
                                ) {
                                    return (
                                        <Tag
                                            color="volcano"
                                            title={
                                                result.errorMessage ?? undefined
                                            }
                                        >
                                            Failed
                                        </Tag>
                                    );
                                }
                                const isRejected =
                                    result.decision ===
                                    DocumentAuditValidationResultDtoDecisionEnum.Rejected;
                                return (
                                    <Tag color={isRejected ? 'red' : 'green'}>
                                        {isRejected ? 'Rejected' : 'Approved'}
                                    </Tag>
                                );
                            },
                        },
                        {
                            title: 'Status',
                            dataIndex: 'status',
                            key: 'status',
                            render: (
                                value: DocumentAuditListItemDtoStatusEnum,
                                record: DocumentAuditListItemDto
                            ) => {
                                const tag = STATUS_META[value];
                                const result = record.humanResult;
                                const reviewerName = formatName(
                                    result?.reviewedBy ?? null
                                );
                                return (
                                    <Space direction="vertical" size={0}>
                                        <Space size={4}>
                                            <Tag color={tag.color}>
                                                {tag.label}
                                            </Tag>
                                            {record.isExpired && (
                                                <Tag color="volcano">
                                                    Expired
                                                </Tag>
                                            )}
                                            {record.isOutdated && (
                                                <Tag>Outdated</Tag>
                                            )}
                                        </Space>
                                        {result && (
                                            <Typography.Text
                                                type="secondary"
                                                style={{ fontSize: 12 }}
                                            >
                                                {dayjs(result.updatedAt).format(
                                                    'YYYY-MM-DD HH:mm'
                                                )}
                                                {reviewerName
                                                    ? ` by ${reviewerName}`
                                                    : ''}
                                            </Typography.Text>
                                        )}
                                    </Space>
                                );
                            },
                        },
                        {
                            title: 'Notes',
                            key: 'notes',
                            render: (
                                _: unknown,
                                record: DocumentAuditListItemDto
                            ) => record.humanResult?.notes,
                        },
                    ]}
                />
                <ReviewDrawer
                    storedDocumentId={selectedId}
                    onClose={() => setSelectedId(null)}
                    onDecisionRecorded={() => {
                        setSelectedId(null);
                        setRefetchToken((t) => t + 1);
                    }}
                />
            </Content>
        </Layout>
    );
};

type ReviewDrawerProps = {
    storedDocumentId: string | null;
    onClose: () => void;
    onDecisionRecorded: () => void;
};

const ReviewDrawer: FC<ReviewDrawerProps> = ({
    storedDocumentId,
    onClose,
    onDecisionRecorded,
}) => {
    const api = useApiClient();
    const { notification } = useToast();
    const [detail, setDetail] = useState<DocumentAuditDetailDto | null>(null);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form] = Form.useForm<{ notes?: string }>();
    const [rejectModalOpen, setRejectModalOpen] = useState(false);
    const [approveConfirmOpen, setApproveConfirmOpen] = useState(false);
    const [nurseMessage, setNurseMessage] = useState('');

    useEffect(() => {
        if (!api || !storedDocumentId) {
            setDetail(null);
            return;
        }
        let cancelled = false;
        setLoading(true);
        form.resetFields();
        api.documentAuditControllerGetDetail({ storedDocumentId })
            .then((value) => {
                if (!cancelled) {
                    setDetail(value);
                    form.setFieldsValue({
                        notes: value.humanResult?.notes ?? undefined,
                    });
                }
            })
            .catch((err) => {
                // biome-ignore lint/suspicious/noConsole: error logging
                console.error(
                    '[DocumentAudits] Failed to load document',
                    storedDocumentId,
                    err
                );
                if (!cancelled) {
                    notification.error({
                        message: 'Failed to load document',
                        description: unwrapError(err),
                        duration: 5,
                    });
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [api, storedDocumentId, form, notification]);

    const submitDecision = async (
        decision: DocumentAuditDecisionRequestDtoDecisionEnum,
        emailMessage?: string
    ): Promise<boolean> => {
        if (!api || !storedDocumentId) return false;
        const values = await form.validateFields();
        const isReject =
            decision === DocumentAuditDecisionRequestDtoDecisionEnum.Rejected;

        try {
            setSubmitting(true);
            await api.documentAuditControllerRecordDecision({
                storedDocumentId,
                documentAuditDecisionRequestDto: {
                    decision,
                    notes: values.notes,
                    emailMessage,
                },
            });
            notification.success({
                message: isReject ? 'Document rejected' : 'Document approved',
                description: emailMessage
                    ? 'The nurse was emailed your message.'
                    : undefined,
                duration: 3,
            });
            onDecisionRecorded();
            return true;
        } catch (err) {
            // biome-ignore lint/suspicious/noConsole: error logging
            console.error(
                '[DocumentAudits] Failed to record decision',
                { storedDocumentId, decision },
                err
            );
            notification.error({
                message: 'Failed to record decision',
                description: unwrapError(err),
                duration: 5,
            });
            return false;
        } finally {
            setSubmitting(false);
        }
    };

    const priorDecision = detail?.humanResult?.decision ?? null;
    const priorReviewerName = formatName(
        detail?.humanResult?.reviewedBy ?? null
    );

    const handleApprove = () => {
        if (priorDecision) {
            setApproveConfirmOpen(true);
        } else {
            void submitDecision(
                DocumentAuditDecisionRequestDtoDecisionEnum.Approved
            );
        }
    };

    return (
        <>
            <Drawer
                title={
                    detail ? (
                        <Space size="small" wrap>
                            <Typography.Text strong>
                                {detail.ownedBy?.profileId ? (
                                    <Link
                                        to={`/profiles/${detail.ownedBy.profileId}`}
                                    >
                                        {formatName(detail.ownedBy) ??
                                            'Unknown nurse'}
                                    </Link>
                                ) : (
                                    'Unknown nurse'
                                )}
                            </Typography.Text>
                            <Typography.Text type="secondary">
                                · {detail.documentType.replace(/_/g, ' ')} ·
                                uploaded{' '}
                                {dayjs(detail.uploadedAt).format(
                                    'YYYY-MM-DD HH:mm'
                                )}
                                {formatName(detail.uploadedBy)
                                    ? ` by ${formatName(detail.uploadedBy)}`
                                    : ''}
                            </Typography.Text>
                        </Space>
                    ) : (
                        'Review document'
                    )
                }
                placement="right"
                width="80%"
                open={!!storedDocumentId}
                onClose={onClose}
                destroyOnClose
                extra={
                    detail && (
                        <Space>
                            {priorDecision !==
                                DocumentAuditDecisionRequestDtoDecisionEnum.Rejected && (
                                <Button
                                    danger
                                    icon={<CloseOutlined />}
                                    loading={submitting}
                                    onClick={() => setRejectModalOpen(true)}
                                >
                                    {priorDecision
                                        ? 'Change to rejected'
                                        : 'Reject'}
                                </Button>
                            )}
                            {priorDecision !==
                                DocumentAuditDecisionRequestDtoDecisionEnum.Approved && (
                                <Button
                                    type="primary"
                                    icon={<CheckOutlined />}
                                    loading={submitting}
                                    onClick={handleApprove}
                                >
                                    {priorDecision
                                        ? 'Change to approved'
                                        : 'Approve'}
                                </Button>
                            )}
                        </Space>
                    )
                }
            >
                {loading || !detail ? (
                    <Spinner />
                ) : (
                    <Space
                        direction="vertical"
                        style={{ width: '100%' }}
                        size="middle"
                    >
                        {detail.documentType ===
                        DocumentAuditDetailDtoDocumentTypeEnum.Certification ? (
                            <ClaimedCertifications
                                certifications={detail.claimedCertifications}
                            />
                        ) : (
                            <ClaimedVaccinations
                                vaccinations={detail.claimedVaccinations}
                            />
                        )}
                        {detail.humanResult && (
                            <Alert
                                type={
                                    detail.humanResult.decision ===
                                    DocumentAuditDecisionRequestDtoDecisionEnum.Approved
                                        ? 'success'
                                        : 'warning'
                                }
                                showIcon
                                message={
                                    <Space size="small" wrap>
                                        <Typography.Text strong>
                                            Current decision:
                                        </Typography.Text>
                                        <Tag>{detail.humanResult.decision}</Tag>
                                        <Typography.Text type="secondary">
                                            {dayjs(
                                                detail.humanResult.updatedAt
                                            ).format('YYYY-MM-DD HH:mm')}
                                            {formatName(
                                                detail.humanResult.reviewedBy
                                            )
                                                ? ` by ${formatName(detail.humanResult.reviewedBy)}`
                                                : ''}
                                        </Typography.Text>
                                    </Space>
                                }
                                description={
                                    detail.humanResult.notes ||
                                    detail.humanResult.nurseEmailMessage ? (
                                        <Space direction="vertical" size={2}>
                                            {detail.humanResult.notes && (
                                                <Typography.Text>
                                                    Internal notes:{' '}
                                                    {detail.humanResult.notes}
                                                </Typography.Text>
                                            )}
                                            {detail.humanResult
                                                .nurseEmailMessage && (
                                                <Typography.Text type="secondary">
                                                    Emailed to nurse: “
                                                    {
                                                        detail.humanResult
                                                            .nurseEmailMessage
                                                    }
                                                    ”
                                                </Typography.Text>
                                            )}
                                        </Space>
                                    ) : undefined
                                }
                            />
                        )}
                        {detail.llmResult && (
                            <Alert
                                type={
                                    detail.llmResult.decision ===
                                    DocumentAuditValidationResultDtoDecisionEnum.Error
                                        ? 'warning'
                                        : 'info'
                                }
                                message={
                                    detail.llmResult.decision ===
                                    DocumentAuditValidationResultDtoDecisionEnum.Error
                                        ? 'LLM pre-screen failed'
                                        : 'LLM pre-screen (advisory)'
                                }
                                description={
                                    <LlmResultSummary
                                        result={detail.llmResult}
                                    />
                                }
                                showIcon
                            />
                        )}
                        <Form form={form} layout="vertical">
                            <Form.Item
                                name="notes"
                                label="Internal notes (not shared with the nurse)"
                                rules={[{ max: 2000 }]}
                            >
                                <Input.TextArea
                                    rows={2}
                                    placeholder="Notes for the credentialing team — the nurse never sees these."
                                />
                            </Form.Item>
                        </Form>
                        <DocumentPreview detail={detail} />
                    </Space>
                )}
            </Drawer>
            <Modal
                open={rejectModalOpen}
                zIndex={1001}
                title="Reject document"
                okText="Confirm rejection"
                okButtonProps={{ danger: true, loading: submitting }}
                cancelButtonProps={{ disabled: submitting }}
                onCancel={() => setRejectModalOpen(false)}
                onOk={async () => {
                    const ok = await submitDecision(
                        DocumentAuditDecisionRequestDtoDecisionEnum.Rejected,
                        nurseMessage.trim() || undefined
                    );
                    if (ok) {
                        setRejectModalOpen(false);
                        setNurseMessage('');
                    }
                }}
            >
                <label htmlFor="reject-nurse-message">
                    <Typography.Text strong>
                        Message to the nurse
                    </Typography.Text>{' '}
                    <Typography.Text type="secondary">
                        (optional)
                    </Typography.Text>
                </label>
                <Typography.Paragraph type="secondary" style={{ marginTop: 4 }}>
                    {detail?.ownedBy?.email
                        ? `Leave blank to reject without emailing the nurse. If filled in, it's emailed to ${detail.ownedBy.email}.`
                        : 'Leave blank to reject without emailing the nurse.'}
                </Typography.Paragraph>
                <Input.TextArea
                    id="reject-nurse-message"
                    rows={5}
                    maxLength={2000}
                    value={nurseMessage}
                    onChange={(e) => setNurseMessage(e.target.value)}
                    placeholder="Explain why the document was rejected and what the nurse should re-upload."
                />
            </Modal>
            <Modal
                open={approveConfirmOpen}
                zIndex={1001}
                title="Change decision to approved?"
                okText="Approve"
                okButtonProps={{ loading: submitting }}
                cancelButtonProps={{ disabled: submitting }}
                onCancel={() => setApproveConfirmOpen(false)}
                onOk={async () => {
                    const ok = await submitDecision(
                        DocumentAuditDecisionRequestDtoDecisionEnum.Approved
                    );
                    if (ok) {
                        setApproveConfirmOpen(false);
                    }
                }}
            >
                <Typography.Paragraph>
                    {priorReviewerName
                        ? `This document was rejected by ${priorReviewerName}. Approving it overrides that decision.`
                        : 'This document was rejected. Approving it overrides that decision.'}
                </Typography.Paragraph>
            </Modal>
        </>
    );
};

const DocumentPreview: FC<{ detail: DocumentAuditDetailDto }> = ({
    detail,
}) => {
    const isImage = detail.mimeType.startsWith('image/');
    const isPdf = detail.mimeType === 'application/pdf';

    const openInNewTab = (
        <Typography.Link
            href={detail.downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
        >
            Open {detail.fileName} in new tab
        </Typography.Link>
    );

    if (isImage) {
        return (
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <img
                    src={detail.downloadUrl}
                    alt={detail.fileName}
                    style={{
                        maxWidth: '100%',
                        maxHeight: '70vh',
                        objectFit: 'contain',
                        border: '1px solid #f0f0f0',
                    }}
                />
                {openInNewTab}
            </Space>
        );
    }

    if (isPdf) {
        return (
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <iframe
                    title={detail.fileName}
                    src={detail.downloadUrl}
                    style={{
                        width: '100%',
                        height: '70vh',
                        border: '1px solid #f0f0f0',
                    }}
                />
                {openInNewTab}
            </Space>
        );
    }

    return (
        <Alert
            type="warning"
            showIcon
            message={`Inline preview unavailable for ${detail.mimeType}`}
            description={openInNewTab}
        />
    );
};

const LlmResultSummary: FC<{ result: DocumentAuditValidationResultDto }> = ({
    result,
}) => {
    const comparisons = result.comparisons;
    const isError =
        result.decision === DocumentAuditValidationResultDtoDecisionEnum.Error;
    return (
        <Space direction="vertical" size={4}>
            <Typography.Text>
                Decision:{' '}
                <Tag color={isError ? 'volcano' : undefined}>
                    {isError ? 'Failed' : result.decision}
                </Tag>
            </Typography.Text>
            <Typography.Text type="secondary">
                Recorded {dayjs(result.updatedAt).format('YYYY-MM-DD HH:mm')}
            </Typography.Text>
            {isError && result.errorMessage && (
                <Typography.Text type="danger">
                    {result.errorMessage}
                </Typography.Text>
            )}
            {comparisons && (
                <Space direction="vertical" size={2}>
                    <Typography.Text>
                        Name{' '}
                        <Tag color={comparisons.name.matched ? 'green' : 'red'}>
                            {comparisons.name.matched ? 'match' : 'mismatch'}
                        </Tag>
                        {!comparisons.name.matched && (
                            <Typography.Text type="secondary">
                                {' '}
                                claimed{' '}
                                {[
                                    comparisons.name.claimed.firstName,
                                    comparisons.name.claimed.lastName,
                                ]
                                    .filter(Boolean)
                                    .join(' ') || '—'}
                                , extracted{' '}
                                {[
                                    comparisons.name.extracted.firstName,
                                    comparisons.name.extracted.lastName,
                                ]
                                    .filter(Boolean)
                                    .join(' ') || '—'}
                            </Typography.Text>
                        )}
                    </Typography.Text>
                    {comparisons.vaccinations &&
                        (comparisons.vaccinations.length === 0 ? (
                            <Typography.Text type="secondary">
                                No dose dates to compare
                            </Typography.Text>
                        ) : (
                            comparisons.vaccinations.map((d) => (
                                <Typography.Text
                                    key={`${d.vaccinationCode}-${d.claimedDate}`}
                                >
                                    {d.vaccinationCode.replace(/_/g, ' ')}{' '}
                                    {d.claimedDate}{' '}
                                    <Tag color={d.matched ? 'green' : 'red'}>
                                        {d.matched ? 'match' : 'mismatch'}
                                    </Tag>
                                </Typography.Text>
                            ))
                        ))}
                    {comparisons.certifications?.map((c) => (
                        <Typography.Text key={c.claimed.code}>
                            {CERTIFICATION_DISPLAY_NAMES[c.claimed.code] ??
                                c.claimed.code}{' '}
                            <Tag color={c.typeMatched ? 'green' : 'red'}>
                                {c.typeMatched ? 'match' : 'mismatch'}
                            </Tag>
                            {!c.typeMatched && (
                                <Typography.Text type="secondary">
                                    extracted {c.extracted.code ?? '—'}{' '}
                                </Typography.Text>
                            )}
                            expires {c.claimed.expiration}{' '}
                            <Tag color={c.expirationMatched ? 'green' : 'red'}>
                                {c.expirationMatched ? 'match' : 'mismatch'}
                            </Tag>
                            {!c.expirationMatched && (
                                <Typography.Text type="secondary">
                                    extracted {c.extracted.expiration ?? '—'}
                                </Typography.Text>
                            )}
                        </Typography.Text>
                    ))}
                </Space>
            )}
        </Space>
    );
};

const ClaimedCertifications: FC<{
    certifications: DocumentAuditClaimedCertificationDto[];
}> = ({ certifications }) => (
    <Card size="small" title="What the nurse entered">
        {certifications.length === 0 ? (
            <Typography.Text type="secondary">
                No certifications are linked to this document.
            </Typography.Text>
        ) : (
            certifications.map((c) => (
                <div key={c.code}>
                    <Typography.Text strong>
                        {CERTIFICATION_DISPLAY_NAMES[c.code] ?? c.code}
                        {c.expiration ? ` · expires ${c.expiration}` : ''}
                    </Typography.Text>
                    {c.holderName && (
                        <div>
                            <Typography.Text type="secondary">
                                Name on card: {c.holderName}
                            </Typography.Text>
                        </div>
                    )}
                </div>
            ))
        )}
    </Card>
);

const ClaimedVaccinations: FC<{
    vaccinations: DocumentAuditClaimedVaccinationDto[];
}> = ({ vaccinations }) => (
    <Card size="small" title="What the nurse entered">
        {vaccinations.length === 0 ? (
            <Typography.Text type="secondary">
                No vaccinations are linked to this document.
            </Typography.Text>
        ) : (
            vaccinations.map((v) => (
                <div key={v.code}>
                    <Typography.Text strong>
                        {v.code.replace(/_/g, ' ')}
                        {v.hasTiter ? ' (titer)' : ''}
                        {v.expiresAt
                            ? ` · expires ${dayjs(v.expiresAt).format('YYYY-MM-DD')}`
                            : ''}
                    </Typography.Text>
                    {v.doses.length === 0 ? (
                        <div>
                            <Typography.Text type="secondary">
                                No dose dates entered
                            </Typography.Text>
                        </div>
                    ) : (
                        v.doses.map((d, i) => (
                            <div key={`${d.date}-${d.manufacturer}-${i}`}>
                                <Typography.Text type="secondary">
                                    {d.date}
                                    {d.manufacturer
                                        ? ` · ${d.manufacturer.replace(/_/g, ' ')}`
                                        : ''}
                                </Typography.Text>
                            </div>
                        ))
                    )}
                </div>
            ))
        )}
    </Card>
);
